import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { DataHelper } from "src/services/dataHelper";

const MenuPermissionsContext = createContext(undefined);

export const MenuPermissionsProvider = (props) => {
  const { children } = props;
  const [menuPermissions, setMenuPermissions] = useState([]);
  const { i18n } = useTranslation();

  // 🔄 Fetch menu + translate based on current language
  const fetchMenuPermissions = useCallback(async () => {
    try {
      const response = await new DataHelper().getMenusList("Menu");
      let data = response?.data || [];

      const lang = i18n.language?.toLowerCase() === "ar" ? "ar" : "en";

      // 🌐 Translate recursively
      const translateMenu = (menu) => ({
        ...menu,
        Title:
          lang === "ar"
            ? menu.ArbName || menu.Title || menu.EngName
            : menu.EngName || menu.Title || menu.ArbName,
        Items: Array.isArray(menu.Items)
          ? menu.Items.map((child) => translateMenu(child))
          : [],
      });

      data = data.map((menu) => translateMenu(menu));
      setMenuPermissions(data);
    } catch (error) {
      console.error("❌ Failed to fetch menu permissions:", error);
      setMenuPermissions([]);
    }
  }, [i18n.language]);

  // 🧭 Find a specific menu by path (recursive)
  const getPermission = useCallback(
    (targetPath, menus = menuPermissions) => {
      for (const menu of menus) {
        if (menu.Path === targetPath) return menu;
        if (Array.isArray(menu.Items) && menu.Items.length > 0) {
          const found = getPermission(targetPath, menu.Items);
          if (found) return found;
        }
      }
      return null;
    },
    [menuPermissions]
  );

  // 🔁 Re-fetch whenever language changes
  useEffect(() => {
    fetchMenuPermissions();
  }, [fetchMenuPermissions, i18n.language]);

  const value = useMemo(
    () => ({
      menuPermissions,
      reloadPermissions: fetchMenuPermissions,
      getPermission,
    }),
    [menuPermissions, fetchMenuPermissions, getPermission]
  );

  return (
    <MenuPermissionsContext.Provider value={value}>
      {children}
    </MenuPermissionsContext.Provider>
  );
};

MenuPermissionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useMenuPermissions = () => {
  const context = useContext(MenuPermissionsContext);

  if (context === undefined) {
    throw new Error("useMenuPermissions must be used within a MenuPermissionsProvider");
  }

  return context;
};

